// The wcBot: picks the activation with the best expected win chance. Every end state of every unit
// it can activate (reach, as the engine moves) and every final action there is played on a copy with
// the real engine; a tag counts as hit × P(hit) + miss × P(miss), by dice forced to each; a decision
// of our own that an action opens (an outpost's choice) is resolved by the same rule. Once chosen, the
// plan is followed step by step, as the planner bot (ai.ts) does.
//
// Reply check (replyK > 0, the strong wcBot): the best replyK options are re-scored after the enemy's
// best reply, found by the same search from its side. Win chances add to 1, so our chance after the
// reply is 1 minus the enemy's best. The enemy's search stops as soon as one reply shows the option is
// no better than one already checked, and the enemy unit that replied best last time goes first.

import { hexEq } from '../../board';
import { actorTeam, applyAction, listLegalActions, playerOf, teamOf, unitById, other, nextRng, tagChance, DIE } from '../../rules';
import { hqOf } from '../../map';
import type { CtfAction, CtfPlayer, CtfState, Team, Unit, Hex } from '../../types';
import { reach, zoneSet } from '../movement';
import type { EndState, Step } from '../movement';
import { newCtx, posOf } from './context';
import type { Ctx } from './context';
import { quietRank, wcFeaturesAt } from './features';
import { WC_FITTED, WC_FITTED_NAME, WC_START, weightVector, winChance } from './model';
import type { WcWeights } from './model';

type Bot = (player: CtfPlayer, s: CtfState) => CtfAction | null;

// Seeds whose first die roll is a given number, to play a tag with a forced hit or miss.
const DIE_SEED: number[] = (() => {
  const out: number[] = [];
  for (let seed = 1; out.filter(x => x !== undefined).length < DIE; seed++) {
    const d = nextRng(seed, 1, DIE)[0];
    if (out[d] === undefined) out[d] = seed;
  }
  return out;
})();

interface Search { team: Team; ctx: Ctx; w: Float64Array; trim: number }

// Win chance of a position for the searching team.
function chance(s: CtfState, k: Search): number {
  if (s.phase === 'complete') return s.winner === k.team ? 1 : s.winner ? 0 : 0.5;
  return winChance(wcFeaturesAt(posOf(s, k.ctx), k.team), k.w);
}

// After our action: resolve a decision of our own that it opened, else judge the position.
function after(s: CtfState, k: Search, depth = 0): number {
  const p = s.pending;
  // A tag roll waiting for Reroll or Keep (army ability): the side deciding takes its better choice.
  if (p?.kind === 'reroll' && depth < 3) {
    const pid = playerOf(s, p.team).id;
    const keep = expected(s, { type: 'keepRoll', playerId: pid }, k, depth + 1), again = expected(s, { type: 'reroll', playerId: pid }, k, depth + 1);
    return p.team === k.team ? Math.max(keep, again) : Math.min(keep, again);
  }
  if (p && p.team === k.team && depth < 2) {
    let best = -1;
    for (const a of listLegalActions(s, playerOf(s, k.team).id)) best = Math.max(best, expected(s, a, k, depth + 1));
    if (best >= 0) return best;
  }
  return chance(s, k);
}

// The positions one action can lead to, with their chances: a tag (or long shot) hits or misses.
type Outcome = { p: number; s: CtfState };
function outcomesOf(s: CtfState, a: CtfAction): Outcome[] {
  if (a.type === 'reroll' && s.pending?.kind === 'reroll') {
    const p = (DIE + 1 - s.pending.need) / DIE;
    return [{ p, s: applyAction({ ...s, rng: DIE_SEED[DIE] }, a) }, { p: 1 - p, s: applyAction({ ...s, rng: DIE_SEED[1] }, a) }];
  }
  if (a.type === 'tag' || a.type === 'longShot') {
    const attacker = unitById(s, a.type === 'tag' ? s.active!.unitId : (s.pending as { unitId: string }).unitId);
    const p = tagChance(s, attacker, unitById(s, a.targetId));
    const hit = applyAction({ ...s, rng: DIE_SEED[DIE] }, a), miss = applyAction({ ...s, rng: DIE_SEED[1] }, a);
    return hit === s ? [] : [{ p, s: hit }, { p: 1 - p, s: miss }];
  }
  const next = applyAction(s, a);
  return next === s ? [] : [{ p: 1, s: next }];
}

// Expected win chance of one action (-1: illegal).
function expected(s: CtfState, a: CtfAction, k: Search, depth = 0): number {
  const outs = outcomesOf(s, a);
  return outs.length ? outs.reduce((v, o) => v + o.p * after(o.s, k, depth), 0) : -1;
}

// The position with the active unit moved to an end state.
function placed(s: CtfState, u: Unit, st: EndState): CtfState {
  const a = s.active!;
  return {
    ...s,
    units: s.units.map(x => (x.id === u.id ? { ...x, pos: st.pos, facing: st.facing } : x)),
    active: { ...a, movesLeft: a.movesLeft - st.path.length, moved: a.moved || st.path.length > 0, moveLocked: a.moveLocked || st.stopped },
  };
}

const stepAction = (st: Step, playerId: string): CtfAction => (st.kind === 'move' ? { type: 'move', playerId, to: st.to } : { type: 'turn', playerId, dir: st.dir });

// `pre`: an army ability used before the path (Sprint, Rally); `startMoves`/`start`: the unit's Move left
// and place once it has been used (where the path starts).
interface Option { unitId: string; path: Step[]; action: CtfAction; value: number; outcomes: Outcome[]; pre: CtfAction[]; startMoves: number; start: { pos: Hex; facing: number } }

// Every option of the active unit, valued. With trimming, quiet options (end, guard) are scored only
// at the `trim` end states that quietRank likes best; every other action is always scored. Stops
// early once an option reaches `stopAt` (the reply check only needs to know the best is that good).
const QUIET = new Set(['endActivation', 'guard']);
function options(s: CtfState, k: Search, stopAt = Infinity): Option[] {
  const a = s.active!;
  const u = unitById(s, a.unitId);
  k.ctx.mover = u.id;
  const pid = playerOf(s, k.team).id;
  const hq = hqOf(s.cells, k.team);
  const states: EndState[] = a.moveLocked ? [{ pos: u.pos!, facing: u.facing, path: [], stopped: true }] : reach(s, u, a.movesLeft, zoneSet(s, other(k.team), true));
  const hyps = states.map(st => placed(s, u, st));
  const at = { startMoves: a.movesLeft, start: { pos: u.pos!, facing: u.facing }, pre: [] as CtfAction[] };
  let quietOk = (_i: number) => true;
  if (k.trim < states.length) {
    const rank = hyps.map((h, i) => quietRank(h, k.ctx, k.team, { ...u, pos: states[i].pos, facing: states[i].facing }, k.w));
    const kept = new Set(rank.map((r, i) => [r, i]).sort((x, y) => y[0] - x[0] || x[1] - y[1]).slice(0, k.trim).map(x => x[1]));
    quietOk = i => kept.has(i);
  }
  const out: Option[] = [];
  for (let i = 0; i < states.length; i++) {
    const st = states[i];
    if (u.carrying === 'flag' && hexEq(st.pos, hq)) {
      // Stepping onto our HQ hex with the flag scores: walk it for real.
      let x = s;
      for (const step of st.path) x = applyAction(x, stepAction(step, pid));
      out.push({ unitId: u.id, path: st.path, action: { type: 'endActivation', playerId: pid }, value: chance(x, k), outcomes: [{ p: 1, s: x }], ...at });
      if (out[out.length - 1].value >= stopAt) return out;
      continue;
    }
    const hyp = hyps[i];
    for (const act of listLegalActions(hyp, pid)) {
      // Moves, and the army abilities used before moving (searched as their own branches in decide).
      if (act.type === 'move' || act.type === 'turn' || act.type === 'activate' || act.type === 'sprint' || act.type === 'rally') continue;
      if (act.type === 'dropFlag' && !hexEq(act.to, hq)) continue;
      if (QUIET.has(act.type) && !quietOk(i)) continue;
      const outcomes = outcomesOf(hyp, act);
      if (!outcomes.length) continue;
      const v = outcomes.reduce((x, o) => x + o.p * after(o.s, k), 0);
      // A guard must hold something up: on a tie, just end.
      out.push({ unitId: u.id, path: st.path, action: act, value: v - (act.type === 'guard' ? 1e-6 : 0), outcomes, ...at });
      if (v >= stopAt) return out;
    }
  }
  return out;
}

// Our win chance in position x after the enemy's best activation from it (if the enemy acts next).
// Stops once that is no more than `floor`; `killer` remembers the enemy unit that replied best.
function replyValue(x: CtfState, k: Search, floor: number, killer: { unitId?: string }): number {
  const foe = other(k.team);
  if (x.phase !== 'playing' || x.pending || actorTeam(x) !== foe) return after(x, k);
  const kf: Search = { ...k, team: foe };
  const acts = listLegalActions(x, playerOf(x, foe).id).filter((a): a is Extract<CtfAction, { type: 'activate' }> => a.type === 'activate');
  if (killer.unitId) acts.sort((a, b) => Number(b.unitId === killer.unitId) - Number(a.unitId === killer.unitId));
  let best = -1;
  for (const a of acts) {
    for (const o of options(applyAction(x, a), kf, 1 - floor)) {
      if (o.value <= best) continue;
      best = o.value;
      killer.unitId = o.unitId;
    }
    if (1 - best <= floor) break;
  }
  return best < 0 ? after(x, k) : 1 - best;
}

// The last unit choice's search value (the chooser's win chance after its best option, and with the
// reply check after the enemy's best reply); null when it explored instead. Self-play records it as a
// training label for the one-step model (bench: selfplay --teacher search).
let searchValue: number | null = null;
export const lastSearchValue = () => searchValue;
export const clearSearchValue = () => { searchValue = null; };

function decide(s: CtfState, k: Search, explore = 0, replyK = 0): Option | null {
  searchValue = null;
  const all: Option[] = [];
  const pid = playerOf(s, k.team).id;
  // An active unit's options, and those after an army ability it can use first (Sprint, Rally).
  const withAbilities = (x: CtfState) => {
    all.push(...options(x, k));
    for (const pre of listLegalActions(x, pid)) {
      if (pre.type !== 'sprint' && pre.type !== 'rally') continue;
      for (const o of options(applyAction(x, pre), k)) all.push({ ...o, pre: [pre] });
    }
  };
  if (s.active) withAbilities(s);
  else for (const a of listLegalActions(s, pid)) if (a.type === 'activate') withAbilities(applyAction(s, a));
  if (!all.length) return null;
  all.sort((x, y) => y.value - x.value || x.path.length - y.path.length);
  if (explore > 0 && !s.active) {
    // A draw from the dice state and the log length: the same game always explores the same way.
    const [roll] = nextRng(s.rng ^ Math.imul(s.log.length + 7, 0x2545F491), 0, 9999);
    if (roll < explore * 10000) return all[Math.min(all.length - 1, 1 + (roll % 2))];
  }
  if (replyK <= 0) { searchValue = all[0].value; return all[0]; }
  let best = all[0], bestV = -1;
  const killer: { unitId?: string } = {};
  for (const o of all.slice(0, replyK)) {
    // Only the last outcome can be cut short: the ones before it are needed in full.
    let v = 0;
    for (let i = 0; i < o.outcomes.length; i++) {
      const oc = o.outcomes[i];
      const floor = i === o.outcomes.length - 1 && bestV >= 0 ? (bestV + 1e-9 - v) / oc.p : -1;
      v += oc.p * replyValue(oc.s, k, floor, killer);
    }
    if (v > bestV + 1e-9) { bestV = v; best = o; }
  }
  searchValue = bestV;
  return best;
}

// The best activation and the value of the trimmed bot's choice, for the bench's trimming check.
export function wcDecide(s: CtfState, team: Team, weights: Partial<WcWeights>, trim = Infinity) {
  return decide(s, { team, ctx: newCtx(), w: weightVector(weights), trim });
}

// The plan being carried out by each team (tied to its game by the dice state and to its bot by name).
interface Plan { rng: number; bot: string; round: number; unitId: string; pre: CtfAction[]; startMoves: number; start: { pos: Hex; facing: number }; path: Step[]; action: CtfAction }
const plans: Partial<Record<Team, Plan>> = {};

function followPlan(s: CtfState, team: Team, playerId: string, bot: string): CtfAction | null {
  const plan = plans[team];
  const a = s.active;
  if (!plan || !a || plan.rng !== s.rng || plan.bot !== bot || plan.unitId !== a.unitId || plan.round !== s.round) return null;
  // First the army ability the plan starts with (Sprint, Rally), if not used yet.
  const pre = plan.pre[0];
  if (pre && !(pre.type === 'sprint' ? a.sprinted : a.moved)) {
    const p = { ...pre, playerId } as CtfAction;
    return applyAction(s, p) !== s ? p : null;
  }
  const done = plan.startMoves - a.movesLeft;
  if (done < 0 || done > plan.path.length) return null;
  let at = plan.start;
  for (const st of plan.path.slice(0, done)) at = st.kind === 'move' ? { pos: st.to, facing: at.facing } : { pos: at.pos, facing: (at.facing + (st.dir === 1 ? 1 : 5)) % 6 };
  const u = unitById(s, a.unitId);
  if (!u.pos || !hexEq(u.pos, at.pos) || u.facing !== at.facing) return null;
  const next = done < plan.path.length ? stepAction(plan.path[done], playerId) : { ...plan.action, playerId } as CtfAction;
  return applyAction(s, next) !== s ? next : null;
}

// A wcBot with the given weights (any not given: WC_START); `name` tells its plans apart. `trim`: quiet
// end states scored per unit (Infinity: all); `explore`: the share of unit choices made among the
// next two best (for self-play data; drawn from the game's dice state, so games still repeat);
// `replyK`: options re-scored after the enemy's best reply (0: none).
export interface WcSettings { trim?: number; explore?: number; replyK?: number }
export function makeWcBot(weights: Partial<WcWeights> = WC_START, name = 'wc', settings: WcSettings = {}): Bot {
  const w = weightVector(weights);
  const trim = settings.trim ?? Infinity, explore = settings.explore ?? 0, replyK = settings.replyK ?? 0;
  return (player, s) => {
    const legal = listLegalActions(s, player.id);
    if (!legal.length) return null;
    const team = teamOf(s, player.id)!;
    const k: Search = { team, ctx: newCtx(), w, trim };
    if (s.pending) {
      let best = legal[0], bestV = -1;
      for (const a of legal) { const v = expected(s, a, k); if (v > bestV) { bestV = v; best = a; } }
      return best;
    }
    if (s.active) {
      const next = followPlan(s, team, player.id, name);
      if (next) return next;
    }
    const o = decide(s, k, explore, replyK);
    if (!o) return legal.find(x => x.type === 'endActivation') ?? legal[0];
    plans[team] = { rng: s.rng, bot: name, round: s.round, unitId: o.unitId, pre: o.pre, startMoves: o.startMoves, start: o.start, path: o.path, action: o.action };
    if (!s.active) return { type: 'activate', playerId: player.id, unitId: o.unitId };
    if (o.pre.length) return { ...o.pre[0], playerId: player.id } as CtfAction;
    return o.path.length ? stepAction(o.path[0], player.id) : o.action;
  };
}

export const getWcBotAction: Bot = makeWcBot();

// The bots the site plays with: the fitted weights, trimmed to 4 quiet end states per unit; the
// strong one also checks its best 4 options against the enemy's best reply.
export const WC_TRIM = 4, WC_REPLY_K = 4;
export const getWcFastBotAction: Bot = makeWcBot(WC_FITTED, `wc-fast-${WC_FITTED_NAME}`, { trim: WC_TRIM });
export const getWcStrongBotAction: Bot = makeWcBot(WC_FITTED, `wc-strong-${WC_FITTED_NAME}`, { trim: WC_TRIM, replyK: WC_REPLY_K });
